import { toast } from 'react-toastify'
import { getErrorMessage } from '../../helpers'
import { BaseApiResponse } from '../shared/shared.type'
import { ReportQueryProps, SalesRepReportQuery } from './reports.type'
import { apiSlice } from '../api/apiSlice'
import { ErrorType } from '../shared/shared.type'
import {
  CategoriesReportResponse,
  CreatorsReportResponse,
  CustomersReportResponse,
  DashboardPercentChangesResponse,
  DashboardReportParams,
  DashboardReportQuery,
  DashboardReportResponse,
  ProductsReportResponse,
  SalesRepReportResponse,
  SettlementReportResponse,
  SingleSalesRepReportQuery,
  SingleSalesRepReportResponse,
  TopCreatorsByChannelResponse,
} from './reports.type'

export const reportsApiSlice = apiSlice.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getDashboardReport: builder.query<
      DashboardReportResponse,
      DashboardReportParams
    >({
      query: (params) => ({
        url: `v1/reports/dashboard/metrics`,
        method: 'GET',
        params: { ...params },
      }),
      providesTags: ['REPORT'],
    }),
    getProductsReport: builder.query<ProductsReportResponse, ReportQueryProps>({
      query: (params) => ({
        url: `v1/reports/products/summary`,
        method: 'GET',
        params: { ...params },
      }),
      providesTags: ['REPORT'],
    }),
    getCustomersReport: builder.query<
      CustomersReportResponse,
      ReportQueryProps
    >({
      query: (params) => ({
        url: `v1/reports/customers/summary`,
        method: 'GET',
        params: { ...params },
      }),
      providesTags: ['REPORT'],
    }),
    getCreatorsReport: builder.query<CreatorsReportResponse, ReportQueryProps>({
      query: (params) => ({
        url: `v1/reports/creators/summary`,
        method: 'GET',
        params: { ...params },
      }),
      providesTags: ['REPORT'],
    }),
    getDashboardPercentChanges: builder.query<
      DashboardPercentChangesResponse,
      DashboardReportParams
    >({
      query: (params) => ({
        url: `v1/reports/dashboard/metrics-percentage-increases`,
        method: 'GET',
        params: { ...params },
      }),
      providesTags: ['REPORT'],
    }),
    exportCreatorsReport: builder.mutation<
      BaseApiResponse,
      DashboardReportQuery
    >({
      query: (params) => ({
        url: `v1/reports/export`,
        method: 'GET',
        params: { ...params },
      }),
      invalidatesTags: [],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const data = await queryFulfilled
          toast.success(data.data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    exportProductsReport: builder.mutation<
      BaseApiResponse,
      DashboardReportQuery
    >({
      query: (params) => ({
        url: `v1/reports/export`,
        method: 'GET',
        params: { ...params },
      }),
      invalidatesTags: [],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const data = await queryFulfilled
          toast.success(data.data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    exportCustomersReport: builder.mutation<
      BaseApiResponse,
      DashboardReportQuery
    >({
      query: (params) => ({
        url: `v1/reports/export`,
        method: 'GET',
        params: { ...params },
      }),
      invalidatesTags: [],
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          const data = await queryFulfilled
          toast.success(data.data.message)
        } catch (error) {
          toast.error(getErrorMessage(error as ErrorType))
        }
      },
    }),
    getSettlementAnalytics: builder.query<
      SettlementReportResponse,
      DashboardReportParams
    >({
      query: (params) => ({
        url: `v1/settlements/analytics`,
        method: 'GET',
        params: { ...params },
      }),
      providesTags: ['SETTLEMENT'],
    }),
    getSalesRepReport: builder.query<
      SalesRepReportResponse,
      SalesRepReportQuery
    >({
      query: (params) => ({
        url: `v2/reports/sales-rep-analytics`,
        method: 'GET',
        params: { ...params },
      }),
      providesTags: ['REPORT'],
    }),
    getSingleSalesRepReport: builder.query<
      SingleSalesRepReportResponse,
      SingleSalesRepReportQuery
    >({
      query: ({ id, ...params }) => ({
        url: `v2/reports/sales-rep-analytics/${id}/history`,
        method: 'GET',
        params: { ...params },
      }),
      providesTags: (_result, _error, arg) => [{ type: 'REPORT', id: arg.id }],
    }),
    getCategoriesReport: builder.query<
      CategoriesReportResponse,
      ReportQueryProps
    >({
      query: (params) => ({
        url: `v1/reports/category/summary`,
        method: 'GET',
        params: { ...params },
      }),
      providesTags: ['REPORT'],
    }),
    getTopCreatorsByChannel: builder.query<
      TopCreatorsByChannelResponse,
      DashboardReportParams & { limit?: number }
    >({
      query: (params) => ({
        url: `v1/reports/top-creators/summary`,
        method: 'GET',
        params: { ...params },
      }),
      providesTags: ['REPORT'],
    }),
  }),
})

export const {
  useGetDashboardReportQuery,
  useGetProductsReportQuery,
  useGetCustomersReportQuery,
  useGetCreatorsReportQuery,
  useGetDashboardPercentChangesQuery,
  useExportCreatorsReportMutation,
  useExportProductsReportMutation,
  useExportCustomersReportMutation,
  useGetSettlementAnalyticsQuery,
  useGetSalesRepReportQuery,
  useGetSingleSalesRepReportQuery,
  useGetCategoriesReportQuery,
  useGetTopCreatorsByChannelQuery,
} = reportsApiSlice
